import React, { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import KinescopePlayer from '@kinescope/react-kinescope-player'

const styles = {
    player: {
        background: '#24282d',
        mb: 3,
        borderRadius: '8px',
        overflow: 'hidden',
    },
}

interface Props {
    link: string
    onStart?: () => void
    onProgress?: (event: CustomEvent<number>) => void
    onPaused?: (event: CustomEvent<boolean>) => void
    onEnded?: () => void
    currentTime?: number
    externalId?: string
}

const KinescopeVideoPlayer: React.FC<Props> = ({
    link,
    currentTime = 0,
    externalId,
    onStart,
    onProgress,
    onPaused,
    onEnded,
}) => {
    const playerRef = useRef<KinescopePlayer>(null)
    const startedRef = useRef(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const videoId = link
        ? new URL(link).pathname.split('/').filter(Boolean).pop()
        : ''

    // iOS Safari: Kinescope fullscreen conflicts with iOS, player asks the parent
    // page to emulate it via postMessage
    useEffect(() => {
        const savedStyles = { current: '' }

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type !== 'KINESCOPE_PLAYER_FULLSCREEN_CHANGE') {
                return
            }
            const iframe = containerRef.current?.querySelector('iframe')
            if (!iframe) {
                return
            }
            if (event.data.value) {
                savedStyles.current = iframe.style.cssText
                iframe.style.cssText =
                    'position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: 9999; background: #000'
            } else {
                iframe.style.cssText = savedStyles.current
            }
        }

        window.addEventListener('message', handleMessage)
        return () => window.removeEventListener('message', handleMessage)
    }, [])

    const handleReady = () => {
        if (currentTime > 0) {
            playerRef.current?.seekTo(currentTime)
        }
    }

    const handlePlay = () => {
        if (!startedRef.current) {
            startedRef.current = true
            onStart?.()
        }
        onPaused?.({ detail: false } as CustomEvent<boolean>)
    }

    return (
        <Box
            sx={styles.player}
            ref={containerRef}
        >
            <KinescopePlayer
                ref={playerRef}
                videoId={videoId}
                externalId={externalId}
                playsInline
                width="100%"
                height={450}
                onReady={handleReady}
                onPlay={handlePlay}
                onPause={() =>
                    onPaused?.({ detail: true } as CustomEvent<boolean>)
                }
                onTimeUpdate={({ currentTime }) =>
                    onProgress?.({
                        detail: currentTime,
                    } as CustomEvent<number>)
                }
                onEnded={() => onEnded?.()}
            />
        </Box>
    )
}

export default KinescopeVideoPlayer
